import { useEffect, useState } from "react";
import axios from "axios";
import { Pencil, X, Trash, Eye, Search } from "lucide-react";
import toast from "react-hot-toast";

export default function VerClientes() {
  const [clientes, setClientes] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [editando, setEditando] = useState(null);
  const [confirmarEliminar, setConfirmarEliminar] = useState(null);
  const [detalleCliente, setDetalleCliente] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    domicilio: "",
    telefono: "",
    localidad: "",
    descripcion_ordeniadora: "",
  });

  useEffect(() => {
    obtenerClientes();
  }, []);

  const obtenerClientes = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/cliente`);
      setClientes(res.data);
    } catch (err) {
      toast.error("Error al cargar los clientes");
    }
  };

  const clientesFiltrados = clientes.filter((c) =>
    c.nombre.toLowerCase().includes(busqueda.toLowerCase())
  );

  const handleEditClick = (cliente) => {
    setEditando(cliente);
    setFormData(cliente);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUpdate = async () => {
    if (!formData.nombre?.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    toast.promise(
      axios.put(`${process.env.REACT_APP_BASE_URL}/cliente/${editando.id}`, formData),
      {
        loading: 'Actualizando...',
        success: (res) => {
          const clienteActualizado = res.data.cliente;
          setClientes((prev) => prev.map((c) => (c.id === clienteActualizado.id ? clienteActualizado : c)));
          setEditando(null);
          return "Actualizado correctamente";
        },
        error: "Error al actualizar",
      }
    );
  };

  const handleDelete = async (cliente) => {
    const nombreUsuario = localStorage.getItem("userName"); // Sacamos a "FERNANDO" del storage

    toast.promise(
      axios.delete(`${process.env.REACT_APP_BASE_URL}/cliente/${cliente.id}`, {
        // Mandamos el nombre en el body (data) igual que hicimos con los servicios
        data: { usuario_nombre: nombreUsuario }
      }),
      {
        loading: 'Eliminando cliente...',
        success: () => {
          setClientes((prev) => prev.filter((c) => c.id !== cliente.id));
          setConfirmarEliminar(null);
          return "Cliente eliminado con éxito";
        },
        error: "Error al eliminar cliente",
      }
    );
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 min-h-0 flex flex-col bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">

        {/* BUSCADOR: filtra mientras se escribe */}
        <div className="shrink-0 px-3 py-2 border-b border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              placeholder="Buscar cliente por nombre..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="w-full pl-9 pr-9 py-1.5 bg-white border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 hover:border-blue-400 outline-none transition-all text-[13px] font-medium"
            />
            {busqueda && (
              <button
                onClick={() => setBusqueda("")}
                title="Limpiar búsqueda"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700 rounded"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <span className="text-[11px] font-semibold text-slate-400">
            <span className="text-slate-900 font-black">{clientesFiltrados.length}</span> de {clientes.length} clientes
          </span>
        </div>

        {/* TABLA: ocupa el alto disponible con encabezado fijo */}
        <div className="flex-1 min-h-0 overflow-auto scroll-fino">
            <table className="w-full text-left border-separate border-spacing-0 table-fixed min-w-[720px]">
              <thead className="bg-slate-900 text-white sticky top-0 z-10">
                <tr className="text-[10px] uppercase tracking-wider font-bold">
                  <th className="px-3 py-2 w-[25%]">Cliente</th>
                  <th className="px-3 py-2 w-[14%]">Localidad</th>
                  <th className="px-3 py-2 w-[41%]">Descripción Ordeñadora</th>
                  <th className="px-3 py-2 w-[10%]">Teléfono</th>
                  <th className="px-3 py-2 w-[10%] text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {clientesFiltrados.map((c, i) => (
                  <tr key={c.id} className={`${i % 2 === 0 ? "bg-white" : "bg-slate-50/70"} hover:bg-blue-50 transition-colors [&>td]:border-b [&>td]:border-slate-100`}>
                    <td className="px-3 py-1.5 text-[12px] font-bold text-slate-900 uppercase truncate" title={c.nombre}>
                      {c.nombre}
                    </td>
                    <td className="px-3 py-1.5 text-[12px] text-slate-600 truncate uppercase" title={c.localidad}>
                      {c.localidad || "-"}
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="text-[12px] text-slate-500 italic truncate max-w-full" title={c.descripcion_ordeniadora}>
                        {c.descripcion_ordeniadora || "-"}
                      </div>
                    </td>
                    <td className="px-3 py-1.5 text-[12px] text-slate-400 truncate" title={c.telefono || ""}>
                      {c.telefono || "-"}
                    </td>
                    <td className="px-2 py-1">
                      <div className="flex justify-center gap-0.5">
                        <button onClick={() => setDetalleCliente(c)} title="Ver detalle" className="p-1 text-blue-600 hover:bg-blue-100 rounded-md transition-colors"><Eye size={15} /></button>
                        <button onClick={() => handleEditClick(c)} title="Editar" className="p-1 text-amber-600 hover:bg-amber-100 rounded-md transition-colors"><Pencil size={15} /></button>
                        <button onClick={() => setConfirmarEliminar(c)} title="Eliminar" className="p-1 text-red-600 hover:bg-red-100 rounded-md transition-colors"><Trash size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {clientesFiltrados.length === 0 && (
              <div className="p-10 text-center text-slate-400 text-xs italic">
                {busqueda ? `Sin resultados para "${busqueda}"` : "No hay clientes cargados"}
              </div>
            )}
        </div>
      </div>

      {/* MODAL DETALLE */}
      {detalleCliente && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-slate-900 px-4 py-3 text-white flex justify-between items-center">
              <h3 className="text-sm font-bold uppercase tracking-tight">{detalleCliente.nombre}</h3>
              <button onClick={() => setDetalleCliente(null)} className="p-1 hover:bg-white/20 rounded-full transition"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-[11px]">
                <div><span className="block font-bold text-gray-700 uppercase text-[12px]">Localidad</span><p className="font-semibold text-gray-700 uppercase">{detalleCliente.localidad}</p></div>
                <div><span className="block font-bold text-gray-700 uppercase text-[12px]">Teléfono</span><p className="font-semibold text-gray-700">{detalleCliente.telefono || "-"}</p></div>
                <div className="col-span-2"><span className="block font-bold text-gray-700 uppercase text-[12px]">Domicilio</span><p className="font-semibold text-gray-700">{detalleCliente.domicilio || "-"}</p></div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                <span className="block font-bold text-gray-700 uppercase text-[12px] mb-1 text-center">Descripción Máquina</span>
                <p className="italic text-[12px] text-gray-700 text-center leading-relaxed">"{detalleCliente.descripcion_ordeniadora}"</p>
              </div>
              <button onClick={() => setDetalleCliente(null)} className="w-full bg-red-600 text-white py-2 rounded-lg font-bold text-[12px] uppercase tracking-widest shadow-md">Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL EDITAR */}
      {editando && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl p-5 w-full max-w-lg">
            <h3 className="text-sm font-bold text-slate-800 mb-4 border-b pb-2 uppercase tracking-tighter">Editar Cliente</h3>
            <div className="space-y-2">
              <div className="space-y-1">
                <label className="text-[9px] font-bold text-gray-700 uppercase">Nombre</label>
                <input type="text" name="nombre" value={formData.nombre} onChange={handleChange} className="w-full border border-gray-200 bg-gray-50 rounded p-1.5 text-xs outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-[9px] font-bold text-gray-700 uppercase">Teléfono</label>
                  <input type="text" name="telefono" value={formData.telefono || ""} onChange={handleChange} className="w-full border border-gray-200 bg-gray-50 rounded p-1.5 text-xs outline-none" />
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] font-bold text-gray-700 uppercase">Localidad</label>
                  <input type="text" name="localidad" value={formData.localidad || ""} onChange={handleChange} className="w-full border border-gray-200 bg-gray-50 rounded p-1.5 text-xs outline-none" />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[9px] font-bold text-gray-700 uppercase">Domicilio</label>
                <input type="text" name="domicilio" value={formData.domicilio || ""} onChange={handleChange} className="w-full border border-gray-200 bg-gray-50 rounded p-1.5 text-xs outline-none" />
              </div>
              <div className="space-y-1">
                <label className="text-[9px] font-bold text-gray-700 uppercase">Descripción Máquina</label>
                <textarea name="descripcion_ordeniadora" value={formData.descripcion_ordeniadora || ""} onChange={handleChange} rows={3} className="w-full border border-gray-200 bg-gray-50 rounded p-1 h-32 text-xs outline-none resize-none" />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setEditando(null)} className="flex-1 py-2 rounded bg-gray-100 font-bold text-[12px] text-gray-500 uppercase">Cancelar</button>
              <button onClick={handleUpdate} className="flex-1 py-2 rounded bg-blue-600 text-white font-bold text-[12px] uppercase shadow-sm hover:bg-blue-700">Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ELIMINAR */}
      {confirmarEliminar && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm flex justify-center items-center z-[70] p-4">
          <div className="bg-white rounded-xl p-5 w-full max-w-xs text-center">
            <div className="w-10 h-10 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-3"><Trash size={20} /></div>
            <h2 className="text-xl font-bold text-gray-800 mb-1 uppercase tracking-tighter">¿Borrar cliente?</h2>
            <p className="text-gray-500 text-[14px] mb-4">SE ELIMINARAN TODOS LOS SERVICIOS ASOCIADOS A  <b>{confirmarEliminar.nombre}</b> DEFINITIVAMENTE.</p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmarEliminar(null)} className="flex-1 mr-4 py-1.5 rounded bg-gray-100 text-gray-500 font-bold text-[14px] uppercase">No</button>
              <button onClick={() => handleDelete(confirmarEliminar)} className="flex-1 py-1.5 rounded bg-red-600 text-white font-bold text-[14px] uppercase shadow-sm">Sí, Borrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}